/*
 * product-card-finishing.js
 *
 * Round 14 (Ansen 2026-10-19, canvas "Website: card finishes 1").
 *
 * MULTI-mode cards (data-card-finishing-mode="multi"):
 *   - Point at a .ag-card-finish-swatch => the card photo previews that
 *     finish (cross-fade on the card's .hover-image layer). Move off the
 *     squares => back to the selected finish's photo. Computers only.
 *   - Click a square =>
 *       1. Mark it .is-selected (clear siblings); CSS draws the traced line.
 *       2. Cross-fade the card's main image to the square's data-image.
 *       3. Update the card's link href to ?variant=<data-variant-id>.
 *       4. e.preventDefault() + e.stopPropagation() so the click doesn't
 *          bubble to the wrapping <a> and navigate the page.
 *
 * SINGLE-mode cards (data-card-finishing-mode="single"):
 *   - Point at the square => the small product page finish card opens above
 *     it: big sample with the accent strip, name, "with X accent", tone pill,
 *     use line (data-finish-* attributes from product_item.liquid).
 *
 * Pictures a card can show are fetched when the pointer first enters that
 * card, so the preview and the popup open without a blank flash.
 *
 * Architecture: ALL handlers are document-level event delegation. Survives
 * Shopify collection-filter AJAX re-renders, infinite scroll, search refresh,
 * any other DOM mutation. No per-element binding flags needed.
 *
 * Image-target selector for MULTI swap: .product-item_image:not(.hover-image)
 * — there are TWO <img class="product-item_image"> on each card (main +
 * hover). We only mutate the main one; the .hover-image is our preview /
 * cross-fade layer (its own hover-swap is killed via CSS on MULTI cards).
 */

(function () {
  'use strict';
  if (window.__agCardFinishingInit) return;
  window.__agCardFinishingInit = true;

  var hoverSupported = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function decoded(src) {
    var im = new Image();
    if ('fetchPriority' in im) im.fetchPriority = 'high';
    im.src = src;
    return im.decode ? im.decode() : Promise.resolve();
  }

  // ---------- Prefetch when the pointer enters a card ----------

  function prefetchCard(card) {
    if (!card || card.__agFinPrefetched) return;
    card.__agFinPrefetched = true;
    card.querySelectorAll('.ag-card-finish-swatch').forEach(function (sw) {
      ['data-image', 'data-finish-large-primary', 'data-finish-large-accent'].forEach(function (a) {
        var src = sw.getAttribute(a);
        if (src) { var im = new Image(); im.src = src; }
      });
    });
  }

  // ---------- SINGLE: the small product page finish card ----------

  var pop = null;   // { el, primary, strip, stripImg, name, accent, divider, pill, usecase }
  var popToken = 0;

  function make(tag, cls, parent) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (parent) parent.appendChild(el);
    return el;
  }

  function ensurePopover() {
    if (pop) return pop;
    var el = make('div', 'ag-card-finish-preview');
    el.setAttribute('role', 'tooltip');
    el.setAttribute('aria-hidden', 'true');
    var sw = make('div', 'ag-card-finish-preview-swatch', el);
    var primary = make('img', 'ag-card-finish-preview-primary', sw);
    primary.alt = '';
    var strip = make('span', 'ag-card-finish-preview-strip', sw);
    var stripImg = make('img', '', strip);
    stripImg.alt = '';
    var body = make('div', 'ag-card-finish-preview-body', el);
    make('span', 'ag-card-finish-preview-eyebrow', body).textContent = 'Finish';
    var name = make('span', 'ag-card-finish-preview-name', body);
    var accent = make('span', 'ag-card-finish-preview-accent', body);
    var divider = make('span', 'ag-card-finish-preview-divider', body);
    var pill = make('span', 'ag-card-finish-preview-pill', body);
    var usecase = make('span', 'ag-card-finish-preview-usecase', body);
    document.body.appendChild(el);
    pop = { el: el, primary: primary, strip: strip, stripImg: stripImg, name: name,
            accent: accent, divider: divider, pill: pill, usecase: usecase };
    return pop;
  }

  // Read the finish from the trigger square. Falls back to the older
  // single-image attributes (data-finish-large / -name) when per-color
  // attributes are missing.
  function readFinish(sw) {
    var hasAccent = sw.getAttribute('data-has-accent') === 'true';
    var primaryLarge = sw.getAttribute('data-finish-large-primary') || sw.getAttribute('data-finish-large') || '';
    var primaryName = sw.getAttribute('data-finish-name-primary') || sw.getAttribute('data-finish-name') || '';
    var accentLarge = hasAccent ? (sw.getAttribute('data-finish-large-accent') || '') : '';
    var accentName = hasAccent ? (sw.getAttribute('data-finish-name-accent') || '') : '';
    return {
      hasAccent: hasAccent && !!accentLarge,
      primaryLarge: primaryLarge,
      primaryName: primaryName,
      accentLarge: accentLarge,
      accentName: accentName,
      tone: (sw.getAttribute('data-finish-tone-label') || '').trim(),
      usecase: (sw.getAttribute('data-finish-usecase') || '').trim()
    };
  }

  function fill(p, f) {
    p.primary.src = f.primaryLarge;
    p.primary.alt = f.primaryName;
    p.strip.hidden = !f.hasAccent;
    if (f.hasAccent) { p.stripImg.src = f.accentLarge; p.stripImg.alt = f.accentName; }
    p.name.textContent = f.primaryName;
    p.accent.hidden = !f.hasAccent;
    p.accent.textContent = f.hasAccent ? 'with ' + f.accentName + ' accent' : '';
    p.pill.hidden = !f.tone;
    p.pill.textContent = f.tone;
    p.usecase.hidden = !f.usecase;
    p.usecase.textContent = f.usecase;
    p.divider.hidden = !f.tone && !f.usecase;
  }

  // Left edge on the card's left edge, kept on screen; above the finish panel
  // (so its label stays readable), or below the square when there is no room.
  function position(p, sw) {
    var card = sw.closest('.product-item_component') || sw;
    var list = sw.closest('.ag-card-finish-list') || sw;
    var r = sw.getBoundingClientRect();
    var c = card.getBoundingClientRect();
    var pw = p.el.offsetWidth || 380;
    var ph = p.el.offsetHeight || 180;
    var left = c.left;
    var maxLeft = document.documentElement.clientWidth - pw - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;
    var top = list.getBoundingClientRect().top - ph - 8;
    if (top < 8) top = r.bottom + 10;
    p.el.style.left = (left + window.pageXOffset) + 'px';
    p.el.style.top = (top + window.pageYOffset) + 'px';
  }

  function showPopover(sw) {
    if (!hoverSupported) return; // touch devices skip
    var f = readFinish(sw);
    if (!f.primaryLarge) return;
    var p = ensurePopover();
    var token = ++popToken;
    var waits = [decoded(f.primaryLarge)];
    if (f.hasAccent) waits.push(decoded(f.accentLarge));
    var shown = false;
    var open = function () {
      if (shown || token !== popToken) return;
      shown = true;
      fill(p, f);
      position(p, sw);
      p.el.classList.add('is-open');
      p.el.setAttribute('aria-hidden', 'false');
    };
    Promise.all(waits).then(open, open);
    setTimeout(open, 3000); // safety net on a slow network
  }

  function hidePopover() {
    ++popToken;
    if (!pop) return;
    pop.el.classList.remove('is-open');
    pop.el.setAttribute('aria-hidden', 'true');
  }

  // ---------- MULTI: photo cross-fade on click ----------

  var SWAP_DURATION_MS = 250;
  function swapImageAnimated(mainImg, hoverImg, newSrc) {
    if (!mainImg || !newSrc) return;
    var currentSrc = mainImg.currentSrc || mainImg.src;
    try {
      if (new URL(currentSrc, window.location.origin).href ===
          new URL(newSrc, window.location.origin).href) {
        return;
      }
    } catch (err) { /* malformed URL — fall through and swap */ }

    if (!hoverImg) {
      mainImg.style.opacity = '0';
      setTimeout(function () {
        mainImg.src = newSrc;
        if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
        requestAnimationFrame(function () { mainImg.style.opacity = '1'; });
      }, SWAP_DURATION_MS);
      return;
    }

    rememberHover(hoverImg);
    hoverImg.src = newSrc;
    if (hoverImg.hasAttribute('srcset')) hoverImg.removeAttribute('srcset');
    hoverImg.style.setProperty('opacity', '1', 'important');

    // The layer keeps showing the new finish while the pointer is still on its
    // square; leaving the square (or the card) gives it photo 2 back.
    setTimeout(function () {
      mainImg.src = newSrc;
      if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
      hoverImg.dataset.agPreview = '1';
    }, SWAP_DURATION_MS);
  }

  function cardParts(sw) {
    var card = sw.closest('.product-item_component');
    if (!card) return null;
    return {
      card: card,
      main: card.querySelector('.product-item_image:not(.hover-image)') || card.querySelector('.product-item_image'),
      over: card.querySelector('.product-item_image.hover-image')
    };
  }

  // The hover layer is also the preview layer: remember photo 2 (its own
  // src/srcset) the first time it is borrowed, and give it back afterwards.
  function rememberHover(over) {
    if (!over || over.dataset.agHoverSrc !== undefined) return;
    over.dataset.agHoverSrc = over.getAttribute('src') || '';
    over.dataset.agHoverSrcset = over.getAttribute('srcset') || '';
  }

  function restoreHover(over) {
    if (!over || over.dataset.agHoverSrc === undefined) return;
    if (over.getAttribute('src') !== over.dataset.agHoverSrc) over.setAttribute('src', over.dataset.agHoverSrc);
    if (over.dataset.agHoverSrcset) over.setAttribute('srcset', over.dataset.agHoverSrcset);
  }

  // ---------- MULTI: preview on hover ----------

  var previewToken = 0;
  var previewHideTimer = null;

  function previewShow(sw) {
    if (!hoverSupported) return;
    clearTimeout(previewHideTimer);
    var parts = cardParts(sw);
    if (!parts || !parts.over || !parts.main) return;
    var src = sw.getAttribute('data-image');
    if (!src) return;
    var token = ++previewToken;
    rememberHover(parts.over);
    var go = function () {
      if (token !== previewToken) return;
      parts.over.src = src;
      if (parts.over.hasAttribute('srcset')) parts.over.removeAttribute('srcset');
      parts.over.dataset.agPreview = '1';
      parts.over.style.setProperty('opacity', '1', 'important');
    };
    decoded(src).then(go, go);
  }

  function previewClear(over) {
    if (!over || !over.dataset.agPreview) return;
    over.style.removeProperty('opacity');
    delete over.dataset.agPreview;
    restoreHover(over);
  }

  // A short wait lets the pointer cross the 6px gap between squares without
  // the photo fading back and forth.
  function previewHideSoon(sw) {
    var parts = cardParts(sw);
    clearTimeout(previewHideTimer);
    previewHideTimer = setTimeout(function () {
      ++previewToken;
      if (parts) previewClear(parts.over);
    }, 80);
  }

  // ---------- Delegated MULTI handler (click) ----------
  document.addEventListener('click', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    var listEl = sw.closest('.ag-card-finish-list[data-card-finishing-mode="multi"]');
    if (!listEl) return;

    e.preventDefault();
    e.stopPropagation();

    ++previewToken;
    clearTimeout(previewHideTimer);
    listEl.querySelectorAll('.ag-card-finish-swatch').forEach(function (s) {
      s.classList.toggle('is-selected', s === sw);
    });

    var card = listEl.closest('.product-item_component') || listEl.parentElement;
    if (!card) return;

    var img = card.querySelector('.product-item_image:not(.hover-image)') ||
              card.querySelector('.product-item_image');
    var hoverImg = card.querySelector('.product-item_image.hover-image');
    var link = card.querySelector('a.product-item_link') ||
               card.querySelector('a.product-item_image-wrapper') ||
               card.querySelector('a[href*="/products/"]');

    var src = sw.getAttribute('data-image');
    swapImageAnimated(img, hoverImg, src);

    var vid = sw.getAttribute('data-variant-id');
    if (vid && link && link.href) {
      try {
        var u = new URL(link.href, window.location.origin);
        u.searchParams.set('variant', vid);
        link.href = u.toString();
      } catch (err) {
        var sep = link.href.indexOf('?') === -1 ? '?' : '&';
        var stripped = link.href.replace(/[?&]variant=\d+/g, '');
        link.href = stripped + sep + 'variant=' + vid;
      }
    }
  });

  // ---------- Delegated hover (both modes) ----------
  // mouseover/mouseout bubble (mouseenter/mouseleave do not), so they work for
  // delegation. The relatedTarget check prevents flicker when moving across
  // child elements inside a square.
  document.addEventListener('mouseover', function (e) {
    if (!e.target.closest) return;
    var card = e.target.closest('.product-item_component');
    if (card && hoverSupported && card.querySelector('.ag-card-finish-list')) prefetchCard(card);

    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    if (e.relatedTarget && sw.contains(e.relatedTarget)) return;
    var listEl = sw.closest('.ag-card-finish-list');
    if (!listEl) return;
    var mode = listEl.getAttribute('data-card-finishing-mode');
    if (mode === 'single') showPopover(sw);
    else if (mode === 'multi') previewShow(sw);
  });

  document.addEventListener('mouseout', function (e) {
    if (!e.target.closest) return;
    var cardOut = e.target.closest('.product-item_component');
    if (cardOut && !(e.relatedTarget && cardOut.contains(e.relatedTarget))) {
      ++previewToken;
      clearTimeout(previewHideTimer);
      previewClear(cardOut.querySelector('.product-item_image.hover-image'));
    }
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    if (e.relatedTarget && sw.contains(e.relatedTarget)) return;
    var listEl = sw.closest('.ag-card-finish-list');
    if (!listEl) return;
    if (listEl.getAttribute('data-card-finishing-mode') === 'multi') previewHideSoon(sw);
    else hidePopover();
  });

  // Keyboard — focusin/focusout bubble (focus/blur do not). The popup opens
  // for keyboard users too; the photo preview stays pointer-only.
  document.addEventListener('focusin', function (e) {
    var sw = e.target.closest && e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    if (sw.closest('.ag-card-finish-list[data-card-finishing-mode="single"]')) showPopover(sw);
  });

  document.addEventListener('focusout', function (e) {
    var sw = e.target.closest && e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    hidePopover();
  });

  // A scrolled page leaves the popup behind; close it.
  window.addEventListener('scroll', function () { if (pop && pop.el.classList.contains('is-open')) hidePopover(); }, { passive: true });
})();
